import React, { useCallback, useState } from 'react';
import { logo } from '../../Data/Images/Home';
import Language from '../../Layout/Header/Language';
import { useLazyGetOrderIdQuery, useMetaApiMutation } from '../../features/metaApiSlice';
import CustomLink from '../CustomLink';
import { v4 as uuidv4 } from 'uuid';
import { getValidFbParam, getMetaParamsAfterRedirect } from '../../utils';
import { useTranslation } from 'react-i18next';
import { useCreateFeedbackMutation } from '../../features/feedbackApiSlice';
import { useHandlePostPurchaseUpsellMutation } from '../../features/customerApiSlice';
import { toast, ToastContainer } from 'react-toastify';
import OrderFinalStep from './components/OrderFinalStep';
import PostPurchaseOfferStep from './components/PostPurchaseOfferStep';
import { getInitialStep, STEP_FINAL, STEP_OFFER } from '../../utils/orderCompleteUpsell';
import useOrderLookupRetry from '../../hooks/useOrderLookupRetry';

const OrderComplete = () => {
  const { t } = useTranslation();
  const quiz_id = localStorage.getItem('quiz_id');
  const [orderData, setOrderData] = useState(null);
  const [step, setStep] = useState(STEP_FINAL);
  const [text, setText] = useState('');

  const [getOrderId] = useLazyGetOrderIdQuery();
  const [createFeedback, { isLoading }] = useCreateFeedbackMutation();
  const [metaApi] = useMetaApiMutation();
  const [handlePostPurchaseUpsell, { isLoading: isOfferActionLoading }] =
    useHandlePostPurchaseUpsellMutation();

  const handleOrderFound = useCallback(
    async (order) => {
      setOrderData(order);
      setStep(getInitialStep(order?.postPurchaseUpsell));

      const orderId = order?.id;
      if (!orderId) return;

      const eventsSentKey = `order_events_sent_${orderId}`;
      const eventsAlreadySent = localStorage.getItem(eventsSentKey);

      if (eventsAlreadySent === 'true') {
        console.log('Events already sent for order:', orderId);
        return;
      }

      const id = uuidv4();
      const email =
        (order?.user?.email || localStorage.getItem('email') || '').trim().toLowerCase() || undefined;
      const metaParams = getMetaParamsAfterRedirect();
      const external_id = metaParams.externalId;
      const fbp = getValidFbParam(metaParams.fbp);
      const fbc = getValidFbParam(metaParams.fbc);
      const event_source_url = window.location.origin + window.location.pathname;

      if (window.fbq) {
        window.fbq(
          'track',
          'Purchase',
          {
            currency: 'EUR',
            value: order?.discounted_price,
          },
          {
            eventID: id,
          },
        );
      }

      try {
        console.log('Meta Purchase', 'start');
        await metaApi({
          event_name: 'Purchase',
          id,
          orderId,
          currency: 'EUR',
          value: order?.discounted_price,
          email,
          page: window.location.href,
          external_id,
          fbp,
          fbc,
          event_source_url,
        }).unwrap();
        console.log('Meta Purchase', 'success');
      } catch (err) {
        console.log('Error sending Meta Purchase event:', err);
      }

      localStorage.setItem(eventsSentKey, 'true');
    },
    [metaApi],
  );

  const lookupOrder = useCallback(() => getOrderId(quiz_id), [getOrderId, quiz_id]);

  useOrderLookupRetry({
    isEnabled: Boolean(quiz_id),
    lookup: lookupOrder,
    onOrderFound: handleOrderFound,
    lookupLabel: `quiz:${quiz_id}`,
  });

  const submitFeedback = () => {
    if (!text || text.trim() === '') {
      toast.error('Please enter your feedback before submitting.');
      return;
    }

    const feedbackEmail =
      (orderData?.user?.email || localStorage.getItem('email') || '').trim().toLowerCase() || undefined;

    createFeedback({
      user_id: orderData?.userId,
      email: feedbackEmail,
      text,
    })
      .then((res) => {
        if (res?.error) {
          toast.error(res?.error?.data?.message || 'Failed to submit feedback. Please try again.');
          return;
        }
        toast.success('Thank you for your feedback!');
        setText('');
      })
      .catch((err) => {
        toast.error(err?.message || 'Failed to submit feedback. Please try again.');
      });
  };

  const handleOfferAction = useCallback(
    async (action, { shouldToast = true } = {}) => {
      if (!orderData?.id || !quiz_id) {
        throw new Error('Order not found for this offer.');
      }

      try {
        const result = await handlePostPurchaseUpsell({
          orderId: orderData.id,
          quizId: quiz_id,
          action,
        }).unwrap();
        setOrderData((current) => ({
          ...current,
          postPurchaseUpsell: {
            ...(current?.postPurchaseUpsell || {}),
            status: result?.status || action,
          },
        }));
        setStep(STEP_FINAL);
        return result;
      } catch (error) {
        if (action === 'expire') {
          setStep(STEP_FINAL);
          return null;
        }

        if (shouldToast) {
          toast.error(error?.data?.message || error?.message || t('OrderUpsell_Error_Generic'));
        }
        throw error;
      }
    },
    [handlePostPurchaseUpsell, orderData?.id, quiz_id, t],
  );

  const handleOfferAccept = useCallback((options) => handleOfferAction('accept', options), [handleOfferAction]);
  const handleOfferDecline = useCallback(() => {
    if (!orderData?.id || !quiz_id) {
      setStep(STEP_FINAL);
      return;
    }

    setOrderData((current) => ({
      ...current,
      postPurchaseUpsell: {
        ...(current?.postPurchaseUpsell || {}),
        status: 'declined',
      },
    }));
    setStep(STEP_FINAL);

    handlePostPurchaseUpsell({
      orderId: orderData.id,
      quizId: quiz_id,
      action: 'decline',
    })
      .unwrap()
      .catch((error) => {
        console.log('Error declining post-purchase offer:', error);
      });
  }, [handlePostPurchaseUpsell, orderData?.id, quiz_id]);
  const handleOfferExpire = useCallback(() => handleOfferAction('expire'), [handleOfferAction]);

  return (
    <>
      <div className="t-sticky t-top-0 t-z-10 t-w-full t-bg-secondary t-flex t-items-center t-justify-center t-py-2">
        <div className="container t-flex t-items-center t-justify-center">
          <CustomLink to="/">
            <img src={logo} alt="" width={60} />
          </CustomLink>
          <div className="t-ml-auto">
            <Language />
          </div>
        </div>
      </div>
      <div className="container t-w-full !t-max-w-[600px] t-mx-auto section-padding t-flex t-flex-col t-gap-4 lg:t-gap-8 t-pb-8">
        {!orderData ? (
          <div className="t-rounded-xl t-bg-white t-p-6 t-text-center t-text-sm t-text-gray-500">
            {t('OrderComplete_Loading')}
          </div>
        ) : step === STEP_OFFER ? (
          <PostPurchaseOfferStep
            orderData={orderData}
            onAccept={handleOfferAccept}
            onDecline={handleOfferDecline}
            onExpire={handleOfferExpire}
            isLoading={isOfferActionLoading}
          />
        ) : (
          <OrderFinalStep
            text={text}
            setText={setText}
            submitFeedback={submitFeedback}
            isLoading={isLoading}
          />
        )}
      </div>
      <ToastContainer />
    </>
  );
};

export default OrderComplete;
